import { useState } from 'react';
import { motion } from 'framer-motion';
import { Target, Rocket, Sparkles, Award, Users, BookOpen } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import ScrollToTop from '../components/ScrollToTop';
import Modal from '../components/Modal';

export default function AboutPage({ theme, toggleTheme }) {
  const [modalContent, setModalContent] = useState(null);
  const openModal = (type) => setModalContent(type);
  const closeModal = () => setModalContent(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  return (
    <div className="flex flex-col relative w-full min-h-screen font-sans" dir="rtl">
      <div className="sticky top-0 z-50">
        <Navbar theme={theme} toggleTheme={toggleTheme} />
      </div>

      <main className="flex-grow pt-20 pb-24 overflow-hidden relative">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-slate-50 dark:bg-zinc-950 -z-20"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/10 dark:bg-blue-500/5 blur-[120px] rounded-full point-events-none -z-10 translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-500/10 dark:bg-purple-500/5 blur-[150px] rounded-full point-events-none -z-10 -translate-x-1/2 translate-y-1/2"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 sm:mt-20">

          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center max-w-3xl mx-auto mb-20 sm:mb-32 relative"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 dark:bg-blue-900/30 text-primary-500 dark:text-primary-500 font-medium text-md mb-6 border border-primary-200 dark:border-primary-800/50">
              <Sparkles className="w-4 h-4" />
              <span>قصة شغف وإبداع</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-black text-slate-900 dark:text-white leading-tight mb-8">
              رواد التعليم الإبداعي في <br className="hidden sm:block mt-2" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-primary-600">
                صناعة التصميم
              </span>
            </h1>
            <p className="text-2xl sm:text-xl text-slate-600 dark:text-slate-400 leading-relaxed max-w-2xl mx-auto">
              نؤمن في  لباب سكول بأن التصميم لغة تفوق الكلمات، وأن الإبداع ليس موهبة فحسب، بل مهارة تُصقل بالمعرفة والتوجيه السليم.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-8"
          >
            {/* Box 1: من نحن */}
            <motion.div variants={itemVariants} className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/0 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              <div className="relative h-full bg-white/60 dark:bg-zinc-900/60 backdrop-blur-xl border border-slate-200 dark:border-zinc-800 rounded-3xl p-8 sm:p-10 hover:border-blue-500/30 dark:hover:border-blue-500/30 transition-all duration-300 flex flex-col items-start overflow-hidden">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white mb-8 shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-500 shrink-0">
                  <Users className="w-7 h-7" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">من هي لباب؟</h2>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed z-10 flex-grow text-lg text-justify rtl:text-right">
                  لباب سكول ليست مجرد منصة تعليمية؛ إنها مجتمع يحتضن المبدعين والشغوفين في عالم التصميم وبناء الهوية البصرية. تأسسنا لتلبية الحاجة الماسة لمحتوى تعليمي عربي احترافي يربط بين التنظير الأكاديمي والممارسة العملية في سوق العمل الحقيقي. نأخذ بيد المصمم من الأساسيات وحتى الاحتراف، ليصبح قادراً على صناعة حلول بصرية مبهرة وذات قيمة مضافة ترفع من مستوى الوعي البصري.
                </p>
                <div className="absolute -bottom-10 -left-10 text-blue-500/5 dark:text-zinc-800/20 -rotate-12 group-hover:text-blue-500/10 dark:group-hover:text-zinc-800/30 transition-colors duration-500 pointer-events-none z-0">
                  <Users className="w-64 h-64" />
                </div>
              </div>
            </motion.div>

            {/* Box 2: رؤيتنا (Vision) */}
            <motion.div variants={itemVariants} className="group relative lg:-translate-y-8">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/0 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              <div className="relative h-full bg-primary-900 dark:bg-zinc-900 backdrop-blur-xl border border-slate-800 dark:border-zinc-700 rounded-3xl p-8 sm:p-10 hover:border-purple-500/50 transition-all duration-300 flex flex-col items-start overflow-hidden shadow-2xl">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-500 flex items-center justify-center text-white mb-8 shadow-lg shadow-primary-500/30 group-hover:scale-110 transition-transform duration-500 shrink-0">
                  <Target className="w-7 h-7" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">رؤية لباب</h2>
                <p className="text-slate-300 dark:text-slate-400 leading-relaxed z-10 flex-grow text-base/8 text-justify rtl:text-right">
                  أن نكون الوجهة الأولى والمرجع الأهم لتعلم التصميم في العالم العربي. نطمح إلى خلق جيل من المصممين القادرين على المنافسة عالمياً، مصممين لا يقتصر عملهم على الجماليات فقط، بل يتعداه إلى حل المشكلات وصناعة تأثير حقيقي وفعّال في الأعمال والمجتمعات من خلال تصميم استراتيجي وهويات بصرية لا تُنسى.
                </p>
                <div className="absolute -bottom-10 -left-10 text-white/5 -rotate-12 group-hover:text-white/10 transition-colors duration-500 pointer-events-none z-0">
                  <Target className="w-64 h-64" />
                </div>
              </div>
            </motion.div>

            {/* Box 3: رسالتنا (Mission) */}
            <motion.div variants={itemVariants} className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-teal-500/0 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              <div className="relative h-full bg-white/60 dark:bg-zinc-900/60 backdrop-blur-xl border border-slate-200 dark:border-zinc-800 rounded-3xl p-8 sm:p-10 hover:border-emerald-500/30 dark:hover:border-emerald-500/30 transition-all duration-300 flex flex-col items-start overflow-hidden">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white mb-8 shadow-lg shadow-emerald-500/30 group-hover:scale-110 transition-transform duration-500 shrink-0">
                  <Rocket className="w-7 h-7" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">رسالة لباب</h2>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed z-10 flex-grow text-base/8 text-justify rtl:text-right">
                  نلتزم بتقديم تجربة تعليمية استثنائية تجمع بين المتعة والعمق، من خلال دورات احترافية، ومشاريع تطبيقية، وتوجيه مستمر. رسالتنا هي تمكين كل شغوف بالتصميم من امتلاك الأدوات والمنهجيات المناسبة، ومساعدته في وضع قدمه بثبات في سوق العمل، مسلّحاً بمهارات تقنية عالية وعقلية مبدعة.
                </p>
                <div className="absolute -bottom-10 -left-10 text-emerald-500/5 dark:text-zinc-800/20 -rotate-12 group-hover:text-emerald-500/10 dark:group-hover:text-zinc-800/30 transition-colors duration-500 pointer-events-none z-0">
                  <Rocket className="w-64 h-64" />
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Quick Stats or Values */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="mt-24 sm:mt-32 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
          >
            {[
              { icon: BookOpen, label: "محتوى حصري", title: "مناهج متقدمة" },
              { icon: Award, label: "شهادات لباب", title: "تدريب احترافي" },
              { icon: Target, label: "بناء الهويات", title: "تطبيقات عملية" },
              { icon: Users, label: "مجتمع تفاعلي", title: "دعم مستمر" }
            ].map((stat, idx) => (
              <motion.div key={idx} variants={itemVariants} className="flex items-center gap-4 bg-white/40 dark:bg-zinc-900/40 backdrop-blur-sm border border-slate-200 dark:border-zinc-800 p-4 sm:p-5 rounded-2xl hover:bg-white dark:hover:bg-zinc-800 hover:shadow-lg hover:shadow-slate-200/50 dark:hover:shadow-none transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center text-primary-600 dark:text-primary-400 shrink-0">
                  <stat.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium mb-1">{stat.label}</div>
                  <div className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">{stat.title}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>

        </div>
      </main>

      <Footer openModal={openModal} />
      <FloatingWhatsApp />
      <ScrollToTop />
      <Modal content={modalContent} onClose={closeModal} />
    </div>
  );
}
